import { useEffect, useState } from 'react'
import { Modal } from '../atoms/Modal'
import { Spinner } from '../atoms/Spinner'
import { Alert } from '../atoms/Alert'
import { useAuth } from '../../../context/AuthContext'
import {
  ORDERSTATUSLABEL,
  type OrderStatus
} from '../../../constants/order-status.constants'

/**
 * Shared "View history" popup — used on the customer order list/detail
 * AND on the operator Order Management Orders page. Hits the
 * appropriate timeline endpoint (`/account/orders/:id/history` or
 * `/order-management/orders/:id/history` depending on `scope`) and
 * renders a date-stamped vertical timeline. Kiran 2026-06-17.
 */
export interface OrderHistoryEntry {
  readonly status: string
  readonly occurred_at: string
  readonly notes: string | null
}

export function OrderHistoryPopup({
  open,
  onClose,
  orderId,
  orderNumber,
  scope
}: {
  readonly open: boolean
  readonly onClose: () => void
  readonly orderId: string
  /** Pretty label shown in the modal title (UYM-XXXXXXXX or similar). */
  readonly orderNumber: string
  /** Which endpoint to call. `customer` = `/account/orders/:id/history`;
   *  `operator` = `/order-management/orders/:id/history`. */
  readonly scope: 'customer' | 'operator'
}): JSX.Element {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<OrderHistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const path =
          scope === 'customer'
            ? `/account/orders/${encodeURIComponent(orderId)}/history`
            : `/order-management/orders/${encodeURIComponent(orderId)}/history`
        const res = await authedFetch(path)
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const body = (await res.json()) as {
          readonly data: { readonly items: OrderHistoryEntry[] }
        }
        if (!cancelled) setItems(body.data.items)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load timeline')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, orderId, scope, authedFetch])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Order timeline · ${orderNumber}`}
      maxWidth="md"
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : error !== null ? (
        <Alert tone="error">{error}</Alert>
      ) : items.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary">No history yet.</p>
      ) : (
        <ol className="space-y-3">
          {items.map((entry, idx) => {
            const isLatest = idx === 0
            return (
              <li key={`${entry.status}-${entry.occurred_at}`} className="flex items-start gap-3">
                <span
                  aria-hidden
                  className={`mt-2 h-2 w-2 flex-shrink-0 rounded-full ${
                    isLatest ? 'bg-forest' : 'bg-text-secondary/40'
                  }`}
                />
                <div className="flex-1">
                  <p className={`font-body text-body ${isLatest ? 'text-charcoal font-semibold' : 'text-charcoal'}`}>
                    {ORDERSTATUSLABEL[entry.status as OrderStatus] ?? entry.status}
                  </p>
                  <p className="font-body text-body-xs text-text-secondary">
                    {formatDateTime(entry.occurred_at)}
                  </p>
                  {entry.notes !== null && entry.notes.trim().length > 0 ? (
                    <p className="mt-1 font-body text-body-sm text-text-secondary whitespace-pre-wrap">
                      {entry.notes}
                    </p>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </Modal>
  )
}

function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}
